import React, { useCallback, useEffect, useState } from 'react';
import {
  Breadcrumb,
  Button,
  Card,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Spin,
  Empty,
} from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PlusOutlined, CommentOutlined, RobotOutlined, ApiOutlined, KeyOutlined } from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { fetchAssistant, getWidgets, listAPIKeys } from '../services/api';
import { listAssistantShares } from '../services/api/chatShares';
import { getBots } from '../services/api/bots';
import { getDataCollectionsTablePagination } from '../components/collections/collectionUtils';
import { botStatusColor, botStatusLabel, platformLabel } from '../utils/botDisplay';

const { Title, Text } = Typography;

function SectionHeader({ title, icon, createLabel, onCreate, viewAllTo }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <Space>
        {icon}
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
      </Space>
      <Space>
        {viewAllTo && (
          <Link to={viewAllTo}>
            <Button type="link" size="small">
              View all
            </Button>
          </Link>
        )}
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onCreate}>
          {createLabel}
        </Button>
      </Space>
    </div>
  );
}

export default function AssistantChannels() {
  const { assistantId: assistantRef } = useParams();
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const [bots, setBots] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [shares, setShares] = useState([]);
  const assistantId = assistant?.id;

  const load = useCallback(async () => {
    if (!initialized || !keycloak?.authenticated || !assistantRef) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const asst = await fetchAssistant(keycloak, assistantRef);
      const [w, b, keys, shareRows] = await Promise.all([
        getWidgets(keycloak, asst.id),
        getBots(keycloak, asst.id),
        listAPIKeys(keycloak, asst.id),
        listAssistantShares(keycloak, assistantRef),
      ]);
      setAssistant(asst);
      setWidgets(Array.isArray(w) ? w : []);
      setBots(Array.isArray(b) ? b : []);
      setApiKeys(Array.isArray(keys) ? keys : []);
      setShares(Array.isArray(shareRows) ? shareRows : []);
    } catch (e) {
      message.error(e.message || 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [initialized, keycloak, assistantRef]);

  useEffect(() => {
    load();
  }, [load]);

  const goWidgets = (create) => {
    const action = create ? 'create' : 'view';
    navigate(`/channels/widgets?assistant_id=${assistantId}&action=${action}`);
  };

  const goApi = (create) => {
    const action = create ? 'create' : 'view';
    navigate(`/channels/api?assistant_id=${assistantId}&action=${action}`);
  };

  const goBotCreate = () => {
    navigate(`/channels/bots/add?assistant_id=${assistantId}`);
  };

  if (!assistantRef) {
    return (
      <div className="page-container">
        <Empty description="Invalid assistant" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/assistants">Assistants</Link> },
          { title: assistant?.name || 'Assistant' },
          { title: 'Channels' },
        ]}
      />
      <div className="page-header" style={{ marginBottom: 24 }}>
        <h2 className="page-header-title">Channels</h2>
        <span className="page-header-subtitle">
          {assistant
            ? `Ways users and systems can reach “${assistant.name}”.`
            : 'Connect this assistant via widgets, bots, and API keys.'}
        </span>
      </div>

      <Spin spinning={loading}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card>
            <SectionHeader
              title="Widgets"
              icon={<CommentOutlined />}
              createLabel={widgets.length ? 'Add widget' : 'Create widget'}
              onCreate={() => goWidgets(widgets.length === 0)}
              viewAllTo="/channels/widgets"
            />
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Embed a chat widget on your website.
            </Text>
            {widgets.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No widgets yet" />
            ) : (
              <Table
                size="small"
                rowKey="id"
                dataSource={widgets}
                pagination={getDataCollectionsTablePagination(5, { totalItemLabel: 'widgets' })}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  {
                    title: 'Status',
                    dataIndex: 'is_active',
                    key: 'status',
                    render: (active) => (
                      <Tag color={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
                    ),
                  },
                  {
                    title: '',
                    key: 'actions',
                    render: (_, row) => (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/channels/widgets/${row.id}/sessions`)}
                      >
                        Sessions
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>

          <Card>
            <SectionHeader
              title="Bots"
              icon={<RobotOutlined />}
              createLabel={bots.length ? 'Add bot' : 'Create bot'}
              onCreate={goBotCreate}
              viewAllTo="/channels/bots"
            />
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Messaging platforms such as Telegram.
            </Text>
            {bots.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No bots yet" />
            ) : (
              <Table
                size="small"
                rowKey="id"
                dataSource={bots}
                pagination={getDataCollectionsTablePagination(5, { totalItemLabel: 'bots' })}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  {
                    title: 'Platform',
                    dataIndex: 'platform',
                    key: 'platform',
                    render: (p) => platformLabel(p),
                  },
                  {
                    title: 'Status',
                    key: 'status',
                    render: (_, row) => (
                      <Tag color={botStatusColor(row)}>{botStatusLabel(row)}</Tag>
                    ),
                  },
                  {
                    title: '',
                    key: 'actions',
                    render: (_, row) => (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/channels/bots/${row.id}/activity`)}
                      >
                        Activity
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>

          <Card>
            <SectionHeader
              title="API Keys"
              icon={<KeyOutlined />}
              createLabel={apiKeys.length ? 'Add API key' : 'Create API key'}
              onCreate={() => goApi(apiKeys.length === 0)}
              viewAllTo="/channels/api"
            />
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Server-side access with API keys.
            </Text>
            {apiKeys.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No API keys yet" />
            ) : (
              <Table
                size="small"
                rowKey="id"
                dataSource={apiKeys}
                pagination={getDataCollectionsTablePagination(5, { totalItemLabel: 'keys' })}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  {
                    title: 'Status',
                    dataIndex: 'is_active',
                    key: 'status',
                    render: (active) => (
                      <Tag color={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
                    ),
                  },
                  {
                    title: '',
                    key: 'actions',
                    render: () => (
                      <Button type="link" size="small" onClick={() => goApi(false)}>
                        Manage
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
          <Card>
            <SectionHeader
              title="Shared chats"
              icon={<CommentOutlined />}
              createLabel="Open chat"
              onCreate={() => navigate(`/chat/${assistantRef}`)}
            />
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Links to a platform conversation. People who open the link can continue with this assistant.
            </Text>
            {shares.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shared chats yet" />
            ) : (
              <Table
                size="small"
                rowKey="public_id"
                dataSource={shares}
                pagination={false}
                columns={[
                  { title: 'Mode', dataIndex: 'mode', key: 'mode' },
                  {
                    title: 'Link',
                    dataIndex: 'enabled',
                    key: 'enabled',
                    render: (on) => (on ? 'On' : 'Off'),
                  },
                  { title: 'Opens', dataIndex: 'open_count', key: 'open_count' },
                  { title: 'Continuations', dataIndex: 'continuation_count', key: 'continuation_count' },
                  {
                    title: '',
                    key: 'review',
                    render: (_, row) => (
                      <Button type="link" size="small" onClick={() => navigate(`/channels/shares/${row.public_id}`)}>
                        Review
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Space>
      </Spin>
    </div>
  );
}
